import React, { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft, Mail, Phone, MapPin, ShoppingBag, Star, Heart } from 'lucide-react'
import { api } from '../lib/api'
import { formatPrice } from '../utils/formatPrice'

// Helper to format date as "dd MMM yyyy"
const formatDate = (dateInput?: string | Date | null): string => {
  if (!dateInput) return '-'
  const d = new Date(dateInput)
  if (isNaN(d.getTime())) return '-'
  const options: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' }
  return d.toLocaleDateString('en-US', options)
}

interface Address {
  id: string
  type: string
  street: string
  city: string
  state: string
  zipCode: string
  country: string
  phone: string
  isDefault: boolean
}

interface Customer {
  id: string
  email: string
  firstName: string
  lastName: string
  phone?: string
  createdAt: string | Date
  emailVerified: boolean
  addresses?: Address[]
}

interface OrderItem {
  id: string
  productId: string
  productName: string
  quantity: number
  price: number
  productImage?: string
}

interface Order {
  id: string
  orderNumber: string
  status: string
  paymentStatus: string
  total: number
  createdAt: string | Date
  items?: OrderItem[]
}

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const { data: customer, isLoading, error } = useQuery({
    queryKey: ['customer', id],
    queryFn: async () => {
      const response = await api.getCustomer(id!)
      return response.data.data as Customer
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: !!id,
  })

  const { data: ordersData, isLoading: ordersLoading } = useQuery({
    queryKey: ['customer-orders', id],
    queryFn: async () => {
      const response = await api.getCustomerOrders(id!, 1, 100)
      return response.data.data as Order[]
    },
    staleTime: 1000 * 60 * 5, // 5 minutes - reduced refetch frequency
    refetchOnWindowFocus: false,
    retry: 1, // Retry once on failure
    enabled: !!id,
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-gray-300 border-t-[#6D3A0D]"></div>
      </div>
    )
  }

  if (error || !customer) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/customers')}
          className="flex items-center gap-2 text-[#6D3A0D] hover:text-[#5D2A0D] font-semibold"
        >
          <ArrowLeft size={20} />
          Back to Customers
        </button>
        <div className="text-center py-12 bg-white rounded-xl border-2 border-red-200">
          <p className="text-red-600 text-lg">Customer not found</p>
        </div>
      </div>
    )
  }

  const defaultAddress = customer.addresses?.find((addr) => addr.isDefault)
  const billingAddress = customer.addresses?.find((addr) => addr.type === 'BILLING')
  const shippingAddress = customer.addresses?.find((addr) => addr.type === 'SHIPPING')

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/customers')}
          className="flex items-center gap-2 text-[#6D3A0D] hover:text-[#5D2A0D] font-semibold transition"
        >
          <ArrowLeft size={20} />
          Back
        </button>
        <h1 className="text-3xl font-bold text-[#6D3A0D]">
          {customer.firstName} {customer.lastName}
        </h1>
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sidebar - Profile Summary */}
        <div className="space-y-6">
          {/* Profile Card */}
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6 shadow">
            <div className="flex flex-col items-center text-center mb-4">
              <div className="w-16 h-16 bg-gradient-to-br from-[#6D3A0D] to-[#8B4513] rounded-full flex items-center justify-center text-white text-2xl font-bold mb-4">
                {customer.firstName.charAt(0)}
              </div>
              <h2 className="text-xl font-bold text-[#6D3A0D]">
                {customer.firstName} {customer.lastName}
              </h2>
              <p className="text-sm text-gray-600">{customer.email}</p>
            </div>

            <div className="space-y-3 pt-4 border-t-2 border-[#F6EEE7]">
              <div className="flex items-start gap-3">
                <Mail size={16} className="text-[#6D3A0D] mt-1 flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs text-gray-600">Email</p>
                  <p className="text-sm font-semibold text-gray-900 break-all">{customer.email}</p>
                </div>
              </div>

              {customer.phone && (
                <div className="flex items-start gap-3">
                  <Phone size={16} className="text-[#6D3A0D] mt-1 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-xs text-gray-600">Phone</p>
                    <p className="text-sm font-semibold text-gray-900">{customer.phone}</p>
                  </div>
                </div>
              )}

              <div className="pt-2 text-xs text-gray-600">
                <p>
                  <strong>Joined:</strong>{' '}
                  {formatDate(customer.createdAt || (customer as any).joinedDate || (customer as any).created_at)}
                </p>
                <p>
                  <strong>Status:</strong>{' '}
                  <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-xs mt-1">
                    Active
                  </span>
                </p>
              </div>
            </div>
          </div>

          {/* Default Address */}
          {defaultAddress && (
            <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6 shadow">
              <h3 className="text-lg font-bold text-[#6D3A0D] mb-3 flex items-center gap-2">
                <MapPin size={20} />
                Default Address
              </h3>
              <div className="space-y-2 text-sm text-gray-700">
                <p className="font-semibold">{defaultAddress.street}</p>
                <p>
                  {defaultAddress.city}, {defaultAddress.state} {defaultAddress.zipCode}
                </p>
                <p>{defaultAddress.country}</p>
                <p className="pt-2 text-[#6D3A0D] font-semibold">{defaultAddress.phone}</p>
              </div>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Personal Information */}
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6 shadow">
            <h3 className="text-lg font-bold text-[#6D3A0D] mb-4">Personal Information</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-gray-600 uppercase">First Name</p>
                <p className="text-sm font-semibold text-gray-900">{customer.firstName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 uppercase">Last Name</p>
                <p className="text-sm font-semibold text-gray-900">{customer.lastName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 uppercase">Email</p>
                <p className="text-sm font-semibold text-gray-900 break-all">{customer.email}</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 uppercase">Phone</p>
                <p className="text-sm font-semibold text-gray-900">{customer.phone || '-'}</p>
              </div>
              {/* <div>
                <p className="text-xs text-gray-600 uppercase">Email Verified</p>
                <p className="text-sm font-semibold">
                  {customer.emailVerified ? (
                    <span className="inline-block px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                      Yes
                    </span>
                  ) : (
                    <span className="inline-block px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                      No
                    </span>
                  )}
                </p>
              </div> */}
              <div>
                <p className="text-xs text-gray-600 uppercase">Member Since</p>
                <p className="text-sm font-semibold text-gray-900">
                  {formatDate(customer.createdAt || (customer as any).joinedDate || (customer as any).created_at)}
                </p>
              </div>
            </div>
          </div>

          {/* Addresses Section */}
          {customer.addresses && customer.addresses.length > 0 && (
            <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6 shadow">
              <h3 className="text-lg font-bold text-[#6D3A0D] mb-4">Addresses</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {billingAddress && (
                  <div className="border-2 border-[#F6EEE7] rounded-lg p-4">
                    <h4 className="font-semibold text-[#6D3A0D] mb-3 text-sm uppercase">
                      Billing Address
                    </h4>
                    <div className="text-sm text-gray-700 space-y-1">
                      <p className="font-semibold">{billingAddress.street}</p>
                      <p>
                        {billingAddress.city}, {billingAddress.state}
                      </p>
                      <p>{billingAddress.zipCode} {billingAddress.country}</p>
                      <p className="pt-2 text-[#6D3A0D]">{billingAddress.phone}</p>
                    </div>
                  </div>
                )}

                {shippingAddress && (
                  <div className="border-2 border-[#F6EEE7] rounded-lg p-4">
                    <h4 className="font-semibold text-[#6D3A0D] mb-3 text-sm uppercase">
                      Shipping Address
                    </h4>
                    <div className="text-sm text-gray-700 space-y-1">
                      <p className="font-semibold">{shippingAddress.street}</p>
                      <p>
                        {shippingAddress.city}, {shippingAddress.state}
                      </p>
                      <p>{shippingAddress.zipCode} {shippingAddress.country}</p>
                      <p className="pt-2 text-[#6D3A0D]">{shippingAddress.phone}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Orders Section */}
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6 shadow">
            <h3 className="text-lg font-bold text-[#6D3A0D] mb-4 flex items-center gap-2">
              <ShoppingBag size={20} />
              All Orders ({ordersData?.length || 0})
            </h3>
            {ordersLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="inline-block h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-[#6D3A0D]"></div>
              </div>
            ) : !ordersData || ordersData.length === 0 ? (
              <p className="text-center py-8 text-gray-600">No orders found</p>
            ) : (
              <div className="space-y-3">
                {ordersData.map((order) => (
                  <div
                    key={order.id}
                    className="border-2 border-[#F6EEE7] rounded-lg p-4 hover:bg-gray-50 transition cursor-pointer"
                    onClick={() => navigate(`/orders/${order.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900">Order #{order.orderNumber}</p>
                        <p className="text-sm text-gray-600">{formatDate(order.createdAt)}</p>
                      </div>
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-semibold text-gray-900">{formatPrice(order.total)}</p>
                          <span
                            className={`inline-block px-3 py-1 rounded text-xs font-semibold ${order.status === 'DELIVERED'
                              ? 'bg-green-100 text-green-700'
                              : order.status === 'CANCELLED'
                                ? 'bg-red-100 text-red-700'
                                : order.status === 'PENDING'
                                  ? 'bg-yellow-100 text-yellow-700'
                                  : 'bg-blue-100 text-blue-700'
                              }`}
                          >
                            {order.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6 shadow">
            <h3 className="text-lg font-bold text-[#6D3A0D] mb-4 flex items-center gap-2">
              <Star size={20} />
              Recent Reviews
            </h3>
            <p className="text-center py-8 text-gray-600">No reviews found</p>
          </div> */}
          {/* 
          <div className="bg-white rounded-xl border-2 border-[#F6EEE7] p-6 shadow">
            <h3 className="text-lg font-bold text-[#6D3A0D] mb-4 flex items-center gap-2">
              <Heart size={20} />
              Wishlist
            </h3>
            <p className="text-center py-8 text-gray-600">Wishlist is empty</p>
          </div> */}
        </div>
      </div>
    </div>
  )
}
